import { canonicalTimezone, timezoneCity, timezoneRegion } from '@ccex/shared';

/** Region keys with a translated header (`app.settings.regions.*`); anything else lands in `other`. */
export const TIMEZONE_REGIONS = [
  'africa',
  'america',
  'antarctica',
  'asia',
  'atlantic',
  'australia',
  'europe',
  'indian',
  'pacific',
  'other',
] as const;
export type TimezoneRegionKey = (typeof TIMEZONE_REGIONS)[number];

export interface TimezoneOption {
  id: string;
  city: string;
  region: TimezoneRegionKey;
}

export interface TimezoneGroup {
  region: TimezoneRegionKey;
  zones: TimezoneOption[];
}

/** The zone the WebView runs in; undefined only on a runtime with no Intl at all. */
export function detectDeviceTimezone(): string | undefined {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return zone ? canonicalTimezone(zone) : undefined;
  } catch {
    return undefined;
  }
}

export function regionKey(zone: string): TimezoneRegionKey {
  const key = timezoneRegion(zone).toLowerCase();
  return (TIMEZONE_REGIONS as readonly string[]).includes(key)
    ? (key as TimezoneRegionKey)
    : 'other';
}

export function toOption(id: string): TimezoneOption {
  return { id, city: timezoneCity(id), region: regionKey(id) };
}

/**
 * Every zone the runtime knows plus `extra` (the device zone and the saved zone, so the current
 * choice is always in the list). Without `Intl.supportedValuesOf` (WebViews older than Safari
 * 15.4 / Chrome 99) only UTC and `extra` remain.
 */
export function listTimezones(extra: Array<string | null | undefined>): TimezoneOption[] {
  const supportedValuesOf = (Intl as { supportedValuesOf?: typeof Intl.supportedValuesOf })
    .supportedValuesOf;
  const known: string[] =
    typeof supportedValuesOf === 'function' ? supportedValuesOf('timeZone') : [];
  const ids = new Set<string>(known.map((zone) => canonicalTimezone(zone)));
  ids.add('UTC');
  for (const zone of extra) if (zone) ids.add(canonicalTimezone(zone));
  return [...ids].sort().map(toOption);
}

/** Case-insensitive substring match on the id and on the spaced city, so "buenos aires" works. */
export function filterTimezones(list: TimezoneOption[], query: string): TimezoneOption[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return list;
  return list.filter(
    (zone) => zone.id.toLowerCase().includes(needle) || zone.city.toLowerCase().includes(needle),
  );
}

export function groupTimezones(list: TimezoneOption[]): TimezoneGroup[] {
  return TIMEZONE_REGIONS.map((region) => ({
    region,
    zones: list.filter((zone) => zone.region === region),
  })).filter((group) => group.zones.length > 0);
}
